import { omit } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm as useRHFForm, UseFormReturn, useWatch } from 'react-hook-form';
import { checkEmptyValues, emailValidation } from '../../../core/formValidation';

export interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  position: string;
  loginCredentialsNeeded: boolean;
  isCashier: boolean;
  isManager: boolean;
  userGroupId: number | null;
  coreSites: { id: number }[];
  cashierSites: { id: number }[];
  emailOptOut: boolean;
}

// Formik-like facade used by consuming components
export type Formik = {
  getFieldProps: (name: keyof FormValues | string) => {
    name: string;
    value: any;
    onChange: (e: any) => void;
    onBlur: (e: any) => void;
  };
  values: FormValues;
  errors: Record<string, { message?: string }>;
  isValid: boolean;
  isSubmitting: boolean;
  submitForm: () => void;
  resetForm: () => void;
  setFieldValue: (name: keyof FormValues | string, value: any) => void;
  setValues: (values: Partial<FormValues>) => void;
  setResetValues: (values: Partial<FormValues>) => void;
};

type FormPage = 'employeeFormPage' | 'userFormPage';

export interface Form {
  formik: Formik;
  handleResetClick: () => void;
  handleSubmitClick: () => void;
  handleTogglePage: () => void;
  formPage: FormPage;
}

export interface FormConfigs {
  isLoading: boolean;
  onSubmit: (values: FormValues) => void;
  initialValues?: Partial<FormValues>;
}

interface UserFields {
  userGroupId: number;
  coreSites: { id: number }[];
  emailOptOut: boolean;
}

export const userFields: (keyof UserFields)[] = ['userGroupId', 'coreSites', 'emailOptOut'];

// Only these fields are required for form submission
const requiredFields: (keyof FormValues)[] = [
  'firstName',
  'lastName',
  'email',
  'phoneNumber',
  'position',
];

const useForm = ({ onSubmit, isLoading, initialValues }: FormConfigs): Form => {
  const [formPage, setFormPage] = useState<FormPage>('employeeFormPage');

  const defaultValues: FormValues = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    position: '',
    loginCredentialsNeeded: false,
    isCashier: false,
    isManager: false,
    userGroupId: null,
    coreSites: [],
    cashierSites: [],
    emailOptOut: false,
    ...initialValues,
  };

  const rhf: UseFormReturn<FormValues> = useRHFForm<FormValues>({
    mode: 'onChange',
    defaultValues,
  });

  // Track the values to reset to (initially the default values)
  const resetValuesRef = useRef<FormValues>(defaultValues);

  // Watch individual required fields for validation
  const firstName = useWatch({ control: rhf.control, name: 'firstName' });
  const lastName = useWatch({ control: rhf.control, name: 'lastName' });
  const email = useWatch({ control: rhf.control, name: 'email' });
  const phoneNumber = useWatch({ control: rhf.control, name: 'phoneNumber' });
  const position = useWatch({ control: rhf.control, name: 'position' });

  // Validate only required fields
  useEffect(() => {
    const vals = rhf.getValues();
    // Only validate the required fields
    const valuesToValidate = requiredFields.reduce((acc, key) => {
      acc[key] = vals[key] as any;
      return acc;
    }, {} as Record<string, any>);
    const errors = checkEmptyValues(valuesToValidate) as Record<string, any>;

    // Add email format validation
    if (vals.email && !errors.email) {
      if (!emailValidation(vals.email)) {
        errors.email = 'Invalid email format';
      }
    }

    // set or clear errors for all required fields
    requiredFields.forEach((key) => {
      if (errors[key]) {
        const errorMessage =
          errors[key] === 'Invalid email format' ? 'Invalid email format' : 'Required';
        rhf.setError(
          key as any,
          { type: 'required', message: errorMessage },
          { shouldFocus: false }
        );
      } else {
        rhf.clearErrors(key as any);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName, email, phoneNumber, position]);

  const handleSubmit = useCallback(() => {
    rhf.handleSubmit((vals) => onSubmit(vals))();
  }, [rhf, onSubmit]);

  const formik: Formik = useMemo(() => {
    const getFieldProps = (name: keyof FormValues | string) => {
      const value = (rhf.getValues() as any)[name];
      const onChange = (e: any) => {
        const next = e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value ?? e;
        rhf.setValue(name as any, next, { shouldValidate: true, shouldDirty: true });
      };
      const onBlur = () => rhf.trigger(name as any);
      return { name: String(name), value, onChange, onBlur };
    };

    const currentVals = rhf.getValues();
    // Only validate the required fields
    const valuesToValidate = requiredFields.reduce((acc, key) => {
      acc[key] = currentVals[key] as any;
      return acc;
    }, {} as Record<string, any>);
    const currentErrors = checkEmptyValues(valuesToValidate) as Record<string, any>;

    // Add email format validation
    if (currentVals.email && !currentErrors.email) {
      if (!emailValidation(currentVals.email)) {
        currentErrors.email = 'Invalid email format';
      }
    }

    // Convert react-hook-form errors to a simple object format
    const formErrors: Record<string, { message?: string }> = {};
    Object.keys(rhf.formState.errors || {}).forEach((key) => {
      const error = rhf.formState.errors[key as keyof FormValues];
      if (error) {
        formErrors[key] = { message: error.message };
      }
    });

    return {
      getFieldProps,
      values: rhf.getValues(),
      errors: formErrors,
      isValid: Object.keys(currentErrors || {}).length === 0,
      isSubmitting: rhf.formState.isSubmitting,
      submitForm: handleSubmit,
      resetForm: () => rhf.reset(resetValuesRef.current),
      setFieldValue: (name, value) => rhf.setValue(name as any, value, { shouldValidate: true }),
      setValues: (values) => rhf.reset({ ...rhf.getValues(), ...(values as any) }),
      setResetValues: (values) => {
        resetValuesRef.current = { ...resetValuesRef.current, ...(values as any) };
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rhf.formState.errors,
    rhf.formState.isSubmitting,
    handleSubmit,
    firstName,
    lastName,
    email,
    phoneNumber,
    position,
  ]);

  const handleResetClick = () => {
    rhf.reset(resetValuesRef.current);
  };

  const handleSubmitClick = () => {
    handleSubmit();
  };

  const handleTogglePage = () => {
    if (formPage === 'employeeFormPage') {
      setFormPage('userFormPage');
    } else {
      setFormPage('employeeFormPage');
    }
  };

  return {
    formik,
    handleResetClick,
    handleSubmitClick,
    handleTogglePage,
    formPage,
  };
};

export default useForm;
